import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, XAxis, YAxis } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "../../../../components/general/chart.tsx";
import { getActiveAccentColors, getCashflowAccentColors } from "../../../../lib/accentPalette";
import { formatYAxisTick } from "../../../../lib/utils.ts";
import {
    QuinnChartType,
    QuinnColourKey,
    QuinnCopy,
    QUINN_CHART_BAR_RADIUS,
    QUINN_CHART_DOT_RADIUS,
    QUINN_CHART_HEIGHT_PX,
    QUINN_CHART_LINE_STROKE_WIDTH,
    QUINN_CHART_PIE_OUTER_RADIUS,
    QUINN_CHART_Y_AXIS_WIDTH,
} from "../../types";
import { centsToDecimal, formatQuinnEuroFromCents } from "../../utils/formatters.ts";

export type QuinnChartPoint = {
    label: string;
    value_cents: number;
};

export type QuinnChartSpec = {
    id?: string;
    chart_type: QuinnChartType;
    title?: string | null;
    series_name?: string | null;
    colour_key?: QuinnColourKey | null;
    points: QuinnChartPoint[];
};

type ChartDatum = {
    label: string;
    value: number;
    value_cents: number;
};

const CASHFLOW = getCashflowAccentColors();

function seriesColour(colourKey?: QuinnColourKey | null): string {
    switch (colourKey) {
        case QuinnColourKey.Inflow:
            return CASHFLOW.inflow.base;
        case QuinnColourKey.Outflow:
            return CASHFLOW.outflow.base;
        case QuinnColourKey.Neutral:
        case null:
        case undefined:
            return getActiveAccentColors()[0];
        default: {
            const _exhaustive: never = colourKey;
            return _exhaustive;
        }
    }
}

function toChartData(points: QuinnChartPoint[]): ChartDatum[] {
    return points.map((point) => ({
        label: point.label,
        value: centsToDecimal(point.value_cents),
        value_cents: point.value_cents,
    }));
}

function formatTooltipValue(_value: unknown, _name: unknown, item: { payload?: ChartDatum }): string {
    const cents = item.payload?.value_cents;
    return typeof cents === "number" ? formatQuinnEuroFromCents(cents) : "";
}

function renderChart(chartType: QuinnChartType, data: ChartDatum[], colour: string, pieRadius: number) {
    const tooltip = <ChartTooltip content={<ChartTooltipContent formatter={formatTooltipValue} />} />;
    const cartesianAxes = (
        <>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} />
            <YAxis
                tickFormatter={formatYAxisTick}
                tickLine={false}
                axisLine={false}
                width={QUINN_CHART_Y_AXIS_WIDTH}
            />
        </>
    );

    switch (chartType) {
        case QuinnChartType.Pie: {
            const palette = getActiveAccentColors();
            return (
                <PieChart>
                    {tooltip}
                    <Pie data={data} dataKey="value" nameKey="label" outerRadius={pieRadius}>
                        {data.map((_, index) => (
                            <Cell key={index} fill={palette[index % palette.length]} />
                        ))}
                    </Pie>
                </PieChart>
            );
        }
        case QuinnChartType.Line:
            return (
                <LineChart data={data}>
                    {cartesianAxes}
                    {tooltip}
                    <Line
                        dataKey="value"
                        stroke={colour}
                        strokeWidth={QUINN_CHART_LINE_STROKE_WIDTH}
                        dot={{ r: QUINN_CHART_DOT_RADIUS }}
                    />
                </LineChart>
            );
        case QuinnChartType.Bar:
            return (
                <BarChart data={data}>
                    {cartesianAxes}
                    {tooltip}
                    <Bar dataKey="value" fill={colour} radius={QUINN_CHART_BAR_RADIUS} />
                </BarChart>
            );
        default: {
            const _exhaustive: never = chartType;
            return _exhaustive;
        }
    }
}

type QuinnChartBlockProps = {
    spec: QuinnChartSpec;
    height?: number;
    showTitle?: boolean;
};

export function QuinnChartBlock({ spec, height = QUINN_CHART_HEIGHT_PX, showTitle = true }: QuinnChartBlockProps) {
    if (!spec.points.length) return null;

    const seriesKey = spec.series_name?.trim() || QuinnCopy.DefaultSeriesName;
    const colour = seriesColour(spec.colour_key);
    const data = toChartData(spec.points);
    const config = { value: { label: seriesKey, color: colour } } satisfies ChartConfig;

    return (
        <figure className="my-3">
            {showTitle && spec.title && (
                <figcaption className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-500">
                    {spec.title}
                </figcaption>
            )}
            <div className="rounded-xl border border-gray-200 bg-white p-3">
                <ChartContainer config={config} className="w-full" style={{ height }}>
                    {renderChart(spec.chart_type, data, colour, Math.round((QUINN_CHART_PIE_OUTER_RADIUS * height) / QUINN_CHART_HEIGHT_PX))}
                </ChartContainer>
            </div>
        </figure>
    );
}
